import { useState } from 'react';
import { Mail, Phone, MessageSquare, FileText, Loader2, Sparkles, MoreHorizontal } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useCommunications } from './useCommunications';
import { CommunicationChannel, CandidateContext, JobContext, ReplyContext } from './types';
import { CommunicationTimeline } from './CommunicationTimeline';
import { ComposeDialog } from './ComposeDialog';
import { InitiateCallDialog } from './InitiateCallDialog';
import { cn } from '@/lib/utils';

interface CommunicationPanelProps {
  candidateId: string;
  candidate: CandidateContext;
  job?: JobContext | null;
  className?: string;
  isOpen?: boolean;
  onClose?: () => void;
}

type FilterId = 'all' | 'emails' | 'calls' | 'messages' | 'notes';

const filters: { id: FilterId; label: string; icon: typeof Mail }[] = [
  { id: 'all', label: 'All', icon: MessageSquare },
  { id: 'emails', label: 'Email', icon: Mail },
  { id: 'calls', label: 'Calls', icon: Phone },
  { id: 'messages', label: 'SMS', icon: MessageSquare },
  { id: 'notes', label: 'Notes', icon: FileText },
];

export function CommunicationPanel({
  candidateId,
  candidate,
  job,
  className,
  isOpen = true,
  onClose,
}: CommunicationPanelProps) {
  const { communications, loading, getStats, fetchCommunications } = useCommunications(candidateId);
  const [composeChannel, setComposeChannel] = useState<CommunicationChannel | null>(null);
  const [replyContext, setReplyContext] = useState<ReplyContext | null>(null);
  const [activeFilter, setActiveFilter] = useState<FilterId>('all');
  const [showCallDialog, setShowCallDialog] = useState(false);

  const stats = getStats();

  const filteredCommunications = communications.filter((comm) => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'emails') return comm.channel === 'email';
    if (activeFilter === 'calls') return comm.channel === 'phone';
    if (activeFilter === 'messages') return comm.channel === 'whatsapp' || comm.channel === 'sms';
    if (activeFilter === 'notes') return comm.channel === 'note';
    return true;
  });

  const handleComposeClose = () => {
    setComposeChannel(null);
    setReplyContext(null);
  };

  const handleSent = () => {
    fetchCommunications();
    setComposeChannel(null);
    setReplyContext(null);
  };

  const handleReply = (reply: ReplyContext) => {
    setReplyContext(reply);
    setComposeChannel('email');
  };

  const getFilterCount = (filterId: FilterId): number => {
    switch (filterId) {
      case 'all': return stats.total;
      case 'emails': return stats.emails;
      case 'calls': return stats.calls;
      case 'messages': return stats.whatsapp;
      case 'notes': return stats.notes;
      default: return 0;
    }
  };

  const candidateName = candidate.name || `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim() || 'Candidate';

  const content = (
    <div className={cn('flex flex-col h-full bg-background', className)}>
      {/* Primary Actions */}
      <div className="px-4 py-3 border-b">
        <div className="flex items-center gap-2">
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={() => setShowCallDialog(true)}
                  disabled={!candidate.phone}
                  className="flex-1 h-10 gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <Phone className="h-4 w-4" />
                  Call
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="bg-popover">
                {candidate.phone ? `Call ${candidate.phone}` : 'No phone number'}
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  onClick={() => setComposeChannel('email')}
                  className="flex-1 h-10 gap-2"
                >
                  <Mail className="h-4 w-4" />
                  Email
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="bg-popover">Send email</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  onClick={() => setComposeChannel('sms')}
                  disabled={!candidate.phone}
                  className="flex-1 h-10 gap-2"
                >
                  <MessageSquare className="h-4 w-4" />
                  SMS
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="bg-popover">
                {candidate.phone ? 'Send SMS' : 'No phone number'}
              </TooltipContent>
            </Tooltip>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-10 w-10 shrink-0">
                  <MoreHorizontal className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-popover">
                <DropdownMenuItem onClick={() => setComposeChannel('whatsapp')} disabled={!candidate.phone}>
                  <MessageSquare className="h-4 w-4 mr-2 text-green-600" />
                  WhatsApp
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setComposeChannel('note')}>
                  <FileText className="h-4 w-4 mr-2 text-amber-600" />
                  Add Note
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setComposeChannel('email')}>
                  <Sparkles className="h-4 w-4 mr-2 text-primary" />
                  AI Draft
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </TooltipProvider>
        </div>
      </div>

      {/* Filter Tabs - No scroll, compact */}
      <div className="flex items-center border-b">
        {filters.map((filter) => {
          const count = getFilterCount(filter.id);
          const isActive = activeFilter === filter.id;
          const Icon = filter.icon;
          
          return (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id)}
              className={cn(
                'flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 text-xs font-medium transition-colors border-b-2 -mb-px',
                isActive
                  ? 'border-primary text-primary bg-primary/5'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              <span>{filter.label}</span>
              {count > 0 && (
                <span className={cn(
                  'min-w-5 h-5 px-1.5 rounded-full text-[10px] font-semibold inline-flex items-center justify-center',
                  isActive ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                )}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Timeline Content */}
      <ScrollArea className="flex-1">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">Loading...</p>
          </div>
        ) : filteredCommunications.length === 0 ? (
          <EmptyState 
            filter={activeFilter} 
            onAction={() => {
              if (activeFilter === 'emails') setComposeChannel('email');
              else if (activeFilter === 'calls') setShowCallDialog(true);
              else if (activeFilter === 'notes') setComposeChannel('note');
              else setComposeChannel('email');
            }}
            hasPhone={!!candidate.phone}
          />
        ) : (
          <CommunicationTimeline
            communications={filteredCommunications}
            onRefresh={fetchCommunications}
            onReply={handleReply}
          />
        )}
      </ScrollArea>

      {composeChannel && (
        <ComposeDialog
          open={!!composeChannel}
          onOpenChange={(open) => !open && handleComposeClose()}
          candidateId={candidateId}
          candidate={candidate}
          job={job}
          channel={composeChannel}
          replyTo={replyContext}
          onSent={handleSent}
        />
      )}

      <InitiateCallDialog
        open={showCallDialog}
        onOpenChange={setShowCallDialog}
        candidateId={candidateId}
        candidate={candidate}
        job={job}
        onCallInitiated={fetchCommunications}
      />
    </div>
  );

  if (onClose) {
    return (
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col bg-background">
          <SheetHeader className="px-4 py-3 border-b shrink-0">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <MessageSquare className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <SheetTitle className="text-left truncate">{candidateName}</SheetTitle>
                <SheetDescription className="text-left text-xs truncate">
                  {candidate.email || candidate.phone || 'Communications'}
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>
          <div className="flex-1 min-h-0 overflow-hidden">{content}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div className={cn('border rounded-lg bg-card overflow-hidden', className)}>
      <div className="flex items-center gap-3 px-4 py-3 border-b bg-muted/30">
        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
          <MessageSquare className="h-4 w-4 text-primary" />
        </div>
        <div>
          <h3 className="font-medium text-sm">Communications</h3>
          <p className="text-xs text-muted-foreground">{stats.total} interactions</p>
        </div>
      </div>
      {content}
    </div>
  );
}

function EmptyState({ filter, onAction, hasPhone }: { filter: string; onAction: () => void; hasPhone: boolean }) {
  const configs: Record<string, { icon: typeof Mail; title: string; desc: string; action?: string }> = {
    all: { icon: MessageSquare, title: 'No activity yet', desc: 'Start a conversation', action: 'Send Email' },
    emails: { icon: Mail, title: 'No emails', desc: 'Send your first email', action: 'Compose' },
    calls: { icon: Phone, title: 'No calls', desc: hasPhone ? 'Make a call' : 'No phone', action: hasPhone ? 'Call' : undefined },
    messages: { icon: MessageSquare, title: 'No messages', desc: hasPhone ? 'Send a message' : 'No phone', action: hasPhone ? 'Send' : undefined },
    notes: { icon: FileText, title: 'No notes', desc: 'Add internal notes', action: 'Add Note' },
  };

  const config = configs[filter] || configs.all;
  const Icon = config.icon;

  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-3">
        <Icon className="h-5 w-5 text-muted-foreground" />
      </div>
      <p className="font-medium text-sm mb-1">{config.title}</p>
      <p className="text-xs text-muted-foreground mb-4">{config.desc}</p>
      {config.action && (
        <Button onClick={onAction} size="sm" variant="outline">
          {config.action}
        </Button>
      )}
    </div>
  );
}
